import React, {useContext, useEffect, useState} from 'react';
import {UserContext} from "../../common/UserContext";
import axios from "axios";
import {ContentContext} from "../../common/ContentContext";
import {BrandContext} from "../../common/BrandContext";
import StudentProgress from "./StudentProgress";
import AutoMarkLegend, {AutoMarkColor} from "./legends/AutoMarkLegend";
import SelfAssessmentLegend, {SelfAssessmentMarkColor} from "./legends/SelfAsessmentLegend";
import ChPercentageBar from "../../common/ChPercentageBar";
import DynamicContent from "../../nav/DynamicContent";
import GenericDialog from "../../common/GenericDialog";
import {isObjectEmpty} from "../../common/utilityFunctions";
import './studentProgress.css';

const StudentProgressGeneric = ()=>{

    const { user } = useContext(UserContext),
        {content} = useContext(ContentContext),
        brandConfig = useContext(BrandContext),
        [error, setError] = useState(''),
        [questionClusterResultDialogOpen, setQuestionClusterResultDialogOpen] = useState(false),
        [questionClusterParams, setQuestionClusterParams] = useState({}),
        [responseData, setResponseData] = useState({}),
        [topicsArray, setTopicsArray] = useState([]),
        [loading, setLoading] = useState(false);

    const makeColumn = ({key, value, children, render, align = 'center', ...rest})=>{
        const obj = { title:value, dataIndex:key, key:key,
            align: align, ...rest};

        // if(children){
        //     obj.children = children;
        // }
        if(render){
            obj.render = render;
        }
        return obj;
    }

    const initialColumns = [
        makeColumn({key:'conceptName', value:brandConfig.conceptName, align:'left'})
    ];

    let scorcherColumn;
    if(brandConfig.hasScorchers){
        scorcherColumn = makeColumn({key:'scorcher', value:brandConfig.scorcherName, render:(props)=>{
                if(props && props.hasScorcher){
                    if(props.scorcherResultDate){
                    const {scorcherScore, scorcherMaxScore, scorcherTime, scorcherResultDate} = props;
                    return (<div>
                        <div className={'flexCentre mediumGap flexWrap'}><div>{scorcherScore}/{scorcherMaxScore}</div><div>{scorcherTime}</div><div>{user.dateFormat.format(scorcherResultDate)}</div></div>
                    </div>)
                    }
                    return <div aria-label={'unattempted'} style={{minWidth:'22px', width:'22px',background:AutoMarkColor.notAttempted, height: '22px'}}></div>
                }
                return null;
            }})
    }

    const fetchData =()=>{
        axios.get('/web/ui/report/student/genericProgress')
            .then((response)=>{
                setResponseData(response.data);
            }).catch((error)=>{
            console.error('Error loading generic progress: ',error);
            setError(error);
        }).finally(()=>{
            setLoading(false);
        })
    }

    useEffect(()=>{
        setLoading(true);
        fetchData();
    },[])

    useEffect(()=>{
        if(Object.keys(responseData).length > 0){
            modifyDataForTable(responseData);
        }
    },[responseData]);

    const QuestionClusterTitles = {
        EXERCISE:'Exercise',
        INLINE:'Exercise',
        QUICK_QUIZ:'Quiz'
    }


    useEffect(()=>{
        if(!isObjectEmpty(questionClusterParams)){
            setQuestionClusterResultDialogOpen(true);
        }
    },[questionClusterParams])


    const modifyDataForTable = (responseData)=>{

        const topicMap = new Map(),
            conceptMap = new Map();

        //group concepts together
        responseData.reportData.forEach((conceptRow)=>{
            const conceptMapKey = conceptRow.conceptId;
            let topicKey = conceptRow.topicId,
                topicName = conceptRow.topicName;
            if(conceptMap.has(conceptMapKey)){
                conceptMap.get(conceptMapKey).items.push(conceptRow);
            }else{
                conceptMap.set(conceptMapKey,{items:[conceptRow], conceptName:conceptRow.conceptName, topic: {topicKey: topicKey, topicName:topicName}});
            }
        });

        const Renders = {
            SELF_MARKING:(props)=>{
                const {obj, className='', title, onClick} = props;
                if(obj !== undefined && obj.answerDate){
                    const barVals = [];
                    const tags = ['Not confident','Unsure','Confident','Very confident','Unassessed'];
                    for(let i = 0; i < 4; i++){
                        barVals.push(obj['markCount'+i]);
                    }
                    barVals.push(obj.totalNotSelfMarked);
                    return (
                        <div onClick={onClick} aria-label={'show results'} className={'flexColumn smallGap '+className} style={{cursor:'pointer', background:'rgb(245 245 245)', padding:'0 4px 1px 4px'}}>
                            <p>{title}</p>
                            <ChPercentageBar tags={tags} values={barVals}
                                             colors={[SelfAssessmentMarkColor.notConfident, SelfAssessmentMarkColor.unsure,
                                              SelfAssessmentMarkColor.confident, SelfAssessmentMarkColor.veryConfident,
                                              SelfAssessmentMarkColor.submitted]}/>
                            <div className={'flexSpaceAround flexWrap smallGap'}><span style={{whiteSpace:'nowrap'}}>{obj.answeredPercent}% complete</span><span aria-label={'Answer date'}>{user.dateFormat.format(obj.answerDate)}</span></div>
                        </div>)
                }else{
                    return (<div className={'flex flexCentre flexAlignCentre smallGap '+className}
                                 style={{backgroundColor: SelfAssessmentMarkColor.notSubmitted, width: '22px', maxWidth: '22px', height: '22px', borderRadius:'5px'}}></div>)
                }
            },
            AUTO_MARKING:(props)=>{
                let {obj, onClick} = props;
                if(obj !== undefined){
                    const {percentVal, answerDate} = obj;
                    let color,
                        wrapperStyle = {background:'rgb(242 242 242)', cursor:'pointer', padding:'6px 4px 1px 4px'};
                    const style = {color:'#ffffff',display:'block',  margin:'auto', width:'fit-content',padding:'2px 16px'};
                    if(answerDate === undefined){
                        style.width = '22px';
                        style.maxWidth = '22px';
                        style.height = '22px';
                        style.padding = 'unset';
                        wrapperStyle = {width:'22px', height:'22px', cursor:'default'};
                        color = AutoMarkColor.notAttempted;
                        onClick = null;
                    }else if(percentVal < 60){
                        color = AutoMarkColor.mark0to59;
                    }else if(percentVal < 90){
                        color = AutoMarkColor.mark50to89;
                    }else if(percentVal <= 100){
                        color = AutoMarkColor.mark90to100;
                    }

                    style.backgroundColor = color;

                return <div className={'flexColumn flexCentre flexWrap'} style={wrapperStyle} onClick={onClick}>
                    {answerDate && <div aria-label={'Quiz score'+answerDate} style={style}>{answerDate?+obj.totalMark+'/'+obj.maxMark+' '+percentVal+'%':''}</div>}
                    {answerDate === undefined && <div aria-label={'Quiz not attempted'} style={style}></div>}
                    {answerDate && <div aria-label={'Answer date'}>{user.dateFormat.format(answerDate)}</div>}
                </div>
                }
            }
        }

        const showQuestionClusterResult = ({questionClusterId, questionClusterResultId, title})=>{
            setQuestionClusterParams({
                title: title,
                questionClusterId: questionClusterId,
                questionClusterResultId: questionClusterResultId
            });
        }

        const selectQuestionClusterRender = (val,item)=>{
            return (<div className={'flexRow flexCentre flexWrap mediumGap flexAlignCentre ch-width-100'}>

                {item.items.map((obj)=>{
                    const Render = Renders[obj.markingType];
                    if(!Render){
                        console.error('Render not found for marking type: ',obj.markingType);
                        return null;
                    }else{
                        return <Render className={'flex1'} obj={obj} onClick={()=>{showQuestionClusterResult(obj)}} />
                    }
                })}
            </div>)

            // return <></>
        }

        const topicColumnMap = new Map();
        let usedTopicClusters = {};
        let lastTopicId = -1;
        // let topic
        conceptMap.values().forEach((value)=>{
            const conceptRow = {items: [], conceptName:value.conceptName};
            // questionClusterCount = 0;
            const topicKey = value.topic.topicKey;
            if(lastTopicId !== topicKey){
                usedTopicClusters = {};
            }
            if (!topicColumnMap.has(topicKey)) {
                topicColumnMap.set(topicKey, []);
            }

            value.items.forEach((item)=>{

                if(item.hasOwnProperty('scorcherResultDate')){
                    conceptRow.scorcher = item;
                }else if(item.hasOwnProperty('questionClusterType')) {
                    // questionClusterCount++;
                    let questionClusterKey = item.questionClusterType;
                    let columnHeader = questionClusterKey;

                    if(QuestionClusterTitles.hasOwnProperty(columnHeader)){
                        columnHeader = QuestionClusterTitles[questionClusterKey];
                    }

                    // const exerciseMap = topicColumnMap.get(topicKey);

                    if (!usedTopicClusters.hasOwnProperty(questionClusterKey)){
                        topicColumnMap.get(topicKey).push(makeColumn({
                            key: questionClusterKey,
                            value: columnHeader,
                            render: selectQuestionClusterRender
                        }));
                        usedTopicClusters[questionClusterKey] = true;
                    }

                    // console.log('item: ',item);

                    let questionClusterObj = {title: columnHeader+' '+(item.questionClusterSeq+1)+' - '+item.conceptName, markingType:item.markingType, percentVal:item.answeredPercent, answerDate:item.answerDate, questionClusterId:item.questionClusterId, questionClusterResultId:item.questionClusterResultId};


                    if(item.markingType === 'SELF_MARKING' && item.answerDate){
                        questionClusterObj.total = item.total;
                        questionClusterObj.totalNotSelfMarked = item.totalNotSelfMarked;
                        questionClusterObj.totalSelfMarked = item.totalSelfMarked;
                        questionClusterObj.answeredPercent = item.answeredPercent;

                        for(let i = 0; i < 4; i++){
                            const totalMark = 'markCount'+i;
                            questionClusterObj[totalMark] = item[totalMark];
                        }

                    }else{
                        if(item.hasOwnProperty('totalMark')){
                            questionClusterObj['totalMark'] = item.totalMark;
                        }
                        if(item.hasOwnProperty('maxMark')) {
                            questionClusterObj['maxMark'] = item.maxMark;
                        }
                    }
                    conceptRow.items.push(questionClusterObj);
                }
            });

            const topicMapKey = value.topic.topicKey;
            if(topicMap.has(topicMapKey)){
                topicMap.get(topicMapKey).items.push(conceptRow);
            }else{
                topicMap.set(topicMapKey,{id:value.topic.topicKey, title:value.topic.topicName, items:[conceptRow]});
            }
            lastTopicId = topicMapKey;
        });
        topicMap.forEach((topic)=>{
            const topicColumns = [...initialColumns, ...topicColumnMap.get(topic.id)];
            if(scorcherColumn !== undefined){
                topicColumns.push(scorcherColumn);
            }
            topic.columns = topicColumns;
        })

        setTopicsArray([...topicMap.values()]);
    }

    const loadQuestionClusterResultCallback = (questionClusterRef) => {
        const $dialogContent = $(questionClusterRef);
        let resultsListHtml = '';

        initLoadResultsListener($dialogContent, questionClusterParams.questionClusterId, callback);
        resultsListHtml = $(questionClusterRef).html();
        function callback (){
            $('.js-showResults', $(questionClusterRef)).off('click',loadResultsList).on('click',loadResultsList);
        }

        function loadResultsList(){
            $dialogContent.html(resultsListHtml);
            initLoadResultsListener($dialogContent, questionClusterParams.questionClusterId, callback);
        }

    };

    return (
        <>
            <GenericDialog id={'progress-report-dialog'} title={'Results '+questionClusterParams.title?questionClusterParams.title:''} open={questionClusterResultDialogOpen}
                   onClose={() => {
                       setQuestionClusterResultDialogOpen(false);
                   }}
                   buttons={[{
                           name: 'Close',
                           extraProps: {type: 'text'}
                       }]}>
                <DynamicContent
                    style={{minWidth:'500px'}}
                    className="js-clusterResultsBlk"
                    url={"/lessonSection/loadInlineQuestionClusterResultsList.action"}
                    params={{
                        ...questionClusterParams
                    }}
                    callback={loadQuestionClusterResultCallback}
                />
            </GenericDialog>
            <StudentProgress
                loading={loading}
                legends={[<AutoMarkLegend heading={'Auto-mark'} />,<SelfAssessmentLegend heading={'Self-assessment'} />]}
                dataArray={topicsArray}
                defaultActiveKey={content?.currentTopic?.topicId}
            />
        </>
    )
}
export default StudentProgressGeneric;
